import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
} from "react-hook-form";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import TextareaField from "../../../components/common/TextareaField";
import {
  OrderFormData,
  PAYMENT_TYPE_OPTIONS,
  ORDER_SHIPMENT_TYPE_OPTIONS,
} from "../../../types/types";
import { useServiceAllListData } from "../../../apis/orderApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useLanguage } from "../../../context/LanguageContext";

interface ShipmentSectionProps {
  register: UseFormRegister<OrderFormData>;
  open: boolean;
  onClose: () => void;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: any;
  clearErrors: UseFormClearErrors<OrderFormData>;
  mode: "add" | "view";
  setMode: (mode: "add" | "view") => void;
  isSubmitted?: boolean;
}

const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  mode,
  open,
  isSubmitted = false,
}) => {
  const { t } = useLanguage();
  const { data } = useServiceAllListData();
  const { data: clients } = useMerchantAllListData();
  const selectedClientId = watch("clientId");
  const selectedClient = clients?.find((c: any) => c?.id === selectedClientId);
  const isNippcoMerchant = selectedClient?.isNippco === true;

  const getServiceOptions = () => {
    let options = data?.map((service: any) => ({
      id: service?.id,
      value: service?.name,
      text: service?.name,
    })) || [];
    if (isNippcoMerchant) {
      const hasSameDay = options.some((opt: any) => opt.value === "SAME_DAY");
      if (!hasSameDay) {
        options = [...options, { id: "SAME_DAY", value: "SAME_DAY", text: t("Same Day") }];
      }
    }
    return options;
  };

  return (
    <div className="grid grid-cols-4 gap-2 bg-[#FFFFFF] p-4 rounded-xl shadow-md h-fit">
      <p className="text-black font-medium text-base uppercase col-span-4 pb-2">
        Shipment Details
      </p>
      <Input
        name="shipment.referenceNumber"
        type="text"
        title="Reference"
        register={register}
        error={errors.shipment?.referenceNumber}
        validation={{
          required: "Reference is required",
          minLength: {
            value: 4,
            message: "Reference must be at least 4 characters",
          },
          maxLength: {
            value: 10,
            message: "Reference cannot exceed 10 characters",
          },
          validate: (value) =>
            (value.trim().length >= 4 && value.trim().length <= 10) ||
            "Reference must be between 4 and 10 characters",
        }}
        className=""
      />

      <Input
        name="shipment.shipmentCost"
        type="text"
        title="Shipment Cost"
        register={register}
        error={errors.shipment?.shipmentCost}
        validation={{
          required: "Shipment cost is required",
          pattern: {
            value: /^\d+(\.\d{1,2})?$/,
            message: "Must be a valid positive number (e.g. 10 or 10.50)",
          },
          validate: (value) =>
            parseFloat(value) > 0 || "Shipment cost must be greater than 0",
        }}
        className=""
      />

      <SelectInput
        name="shipment.paymentType"
        label="Payment Type"
        className=""
        placeholder="Select Payment Type"
        selectOptions={PAYMENT_TYPE_OPTIONS}
        clearErrors={clearErrors}
        register={register}
        value={watch("shipment.paymentType")}
        error={errors.shipment?.paymentType}
        setValue={(val: string) => {
          setValue("shipment.paymentType", val);
          if (val !== "COD") {
            setValue("shipment.codAmount", "");
          }
        }}
        validation={{ required: "Payment Type is required" }}
      />

      {watch("shipment.paymentType") === "COD" && (
        <Input
          name="shipment.codAmount"
          type="number"
          title="COD Amount"
          register={register}
          error={errors.shipment?.codAmount}
          validation={{ required: "COD amount is required" }}
          className=""
        />
      )}

      <Input
        name="shipment.numberOfPieces"
        type="number"
        title="No of Pieces"
        register={register}
        error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
        validation={{
          required: "No of pieces is required",
          min: { value: 1, message: "Must be at least 1" },
          validate: (value) =>
            Number.isInteger(Number(value)) || "Must be a whole number",
        }}
        className=""
      />

      <Input
        name="shipment.totalWeight"
        type="number"
        title="Total Weight (kg)"
        register={register}
        error={errors.shipment?.totalWeight}
        disabled={true}
        className=""
      />

      <SelectInput
        name="shipment.serviceType"
        label="Service"
        className=""
        placeholder="Select Service"
        selectOptions={getServiceOptions()}
        clearErrors={clearErrors}
        register={register}
        validation={{ required: "Service is required" }}
        error={errors.shipment?.serviceType}
        value={watch("shipment.serviceType")}
        setValue={(val: string) => {
          setValue("shipment.serviceType", val);
        }}
      />

      <SelectInput
        name="shipment.shipmentType"
        label="Shipment Type"
        className=""
        placeholder="Select Shipment Type"
        selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS}
        clearErrors={clearErrors}
        register={register}
        validation={{ required: "Shipment type is required" }}
        error={errors.shipment?.shipmentType}
        value={watch("shipment.shipmentType")}
        setValue={(val: string) => {
          setValue("shipment.shipmentType", val);
        }}
      />

      <TextareaField
        rows={2}
        className="col-span-4"
        label="Description"
        register={register}
        placeholder=""
        name="shipment.description"
        error={errors.shipment?.description}
        validation={{ required: "Description is required" }}
      />
    </div>
  );
};

export default ShipmentSection;
